import { match } from 'assert';

import React, { ChangeEvent, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

import { Nullable } from '@tager/web-core';

import ContentContainer from '@/components/ContentContainer';
import { colors } from '@/constants/theme';
import { ReactComponent as Arrow } from '@/assets/svg/arrow_question.svg';
import { ProductMultiplePricesValuesType } from '@/typings/model';

type Props = {
  data: Nullable<{
    label: string;
    options: ProductMultiplePricesValuesType[];
    enabled: boolean;
  }>;
  isOptionsOpen: boolean;
  currentIndex: number;
  setOpen: (fc: (prevState: boolean) => boolean) => void;
  setIndex: (prevState: number) => void;
};

function MultiPriceView({
  data,
  isOptionsOpen,
  currentIndex,
  setIndex,
  setOpen,
}: Props) {
  const selectRef = useRef<HTMLSelectElement>(null);

  return (
    <Component>
      <ContentContainer>
        <Label>{data?.label}</Label>
        <SelectWrapper>
          <StyledArrow
            isopen={isOptionsOpen}
            className="product-multiprice-select-arrow"
          />
          <Select
            ref={selectRef}
            className="product-multiprice-select"
            onFocus={() => {
              setOpen((pre) => !pre);
            }}
            onBlur={(e) => {
              e.target.click();
              setOpen((pre) => false);
            }}
            onChange={(e) => {
              setOpen((pre) => !pre);
              if (e.target.selectedIndex !== currentIndex) {
                setIndex(e.target.selectedIndex);
              }
            }}
          >
            {(data?.options ?? []).map((option, index) => (
              <Option
                key={index}
                selected={index === currentIndex}
                isSelected={index === currentIndex}
              >
                {option.name}
              </Option>
            ))}
          </Select>
        </SelectWrapper>
        {data?.options && (
          <Description>{data.options[currentIndex]?.description}</Description>
        )}
      </ContentContainer>
    </Component>
  );
}

export default MultiPriceView;

const Component = styled.div``;

const Label = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 20px;
`;

const SelectWrapper = styled.div`
  margin-top: 7px;
  min-height: 34px;
  max-width: 480px;
  overflow: hidden;
  border: 1px solid ${colors.gray70};
  border-radius: 5px;
  position: relative;
`;
const Select = styled.select`
  position: absolute;
  padding-left: 15px;
  height: 100%;
  width: 100%;

  cursor: pointer;
  outline: none;
  border: none;

  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  -ms-word-break: normal;
  word-break: normal;

  background: transparent;
  z-index: 10;
`;
const Option = styled.option<{ isSelected: boolean }>`
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 20px;

  ${({ isSelected }) =>
    isSelected
      ? css`
          color: ${colors.primary};
          font-weight: 600;
        `
      : css`
          color: ${colors.black};
        `}
`;

const StyledArrow = styled(Arrow)<{ isopen?: boolean }>`
  cursor: pointer;
  position: absolute;
  top: 50%;
  right: 15px;
  transform: translateY(-50%)
    ${({ isopen }) => (isopen ? ' rotate(180deg)' : ' rotate(0)')};
  path {
    fill: rgba(0, 0, 0, 1);
  }
  z-index: 1;
`;

const Description = styled.div`
  margin-top: 5px;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 20px;
  color: ${colors.grayDark};
`;
